import { memo, useCallback, useMemo } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { dialog, dom } from 'app/utils';
import { Breadcrumb } from 'app/components';
import { Button } from 'app/components/common';
import { Form } from 'app/components/layout';
import { Field, Select, TextBox } from 'app/components/form';
import { professions } from 'app/services';
import { IProfession } from 'app/models';

export default memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const controller = useFormik<IProfession>({
    initialValues: (useLoaderData() ?? {}) as IProfession,
    validationSchema: useMemo(() => yup.object({
      status: yup.number().oneOf([0, 1]).required(),
      type: yup.number().oneOf([1, 2]).required(),
      name: yup.string().max(255).required(),
    }), []),

    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: true,

    onSubmit: () => { },
  }), { handleSubmit, handleReset } = controller;

  const onSave = useCallback(() => {
    if (!controller.isValid) { dialog.toast({ message: t('app.validation.required'), severity: 'error' }); return; }

    professions.save(controller.values).then(({ success }) => {
      if (!success) return;

      dialog.toast({ severity: 'success', message: t('professions.save.success') });
      navigate('..');
    });
  }, [controller, t, navigate]);

  const { touched, errors } = controller;
  const getProps = useCallback((field: keyof IProfession): any => ({ ...controller.getFieldProps(field),
    invalid: !!(touched[field] && errors[field]),
  }), [controller, touched, errors]);

  const form: any = {
    className: dom.className('p-4', 'block', 'flex-auto', 'overflow-auto'),

    onSubmit: handleSubmit,
    onReset: handleReset,
  };

  return (
    <div className="h-full flex flex-column align-items-stretch">
      <div className="flex-none flex surface-200">
        <Breadcrumb className="flex-auto border-none border-noround bg-transparent" />
        <div className="flex flex-none m-2">
          <Button text severity="warning" href=".." icon="pi-times" label={t('app.cancel')} className="mr-2 px-3 py-1" />
          <Button icon="mdi-check" label={t('app.save')} className="px-3 py-1" onClick={onSave} />
        </div>
      </div>
      <form { ...form }>
        <Form responsive={true}>
          <Field text={t('professions.fields.name')} error={controller.errors.name} align="end" asterisk>
            <TextBox { ...getProps("name") } />
          </Field>
          <Field text={t('professions.fields.type')} error={controller.errors.type} align="end" asterisk className="pt-4">
            <Select { ...getProps('type') } options={[
              { label: t('app.profile.employee'), value: 1 },
              { label: t('app.profile.employer'), value: 2 },
            ]} />
          </Field>
          <Field text={t('professions.fields.status')} error={controller.errors.status} align="end" asterisk className="pt-4">
            <Select { ...getProps('status') } options={[
              { label: t('app.status.disabled'), value: 0 },
              { label: t('app.status.enabled'), value: 1 },
            ]} />
          </Field>
        </Form>
      </form>
    </div>
  );
});
